import React, { useMemo } from 'react';
import SelectInput from '@/se/components/inputs/SelectInput';
import { useScope } from '@/hooks/useScope';
import {
  AnalyticsPdfOption,
  analyticsPdfsOptions,
  defaultAnalyticsPdfOption,
  detailedReportPdfOption,
} from '../../../inputs/AnalyticsPdfsMultipleSelectInput';

interface AnalyticsReportPdfSelectProps {
  name?: string;
  /** `AnalyticsReport` id ('' or null = Detailed Report, the default). */
  value?: string | null;
  onChange: (value: string) => void;
  [key: string]: unknown;
}

/**
 * Picks which analytics PDF a scheduled (weekly / monthly) report email attaches.
 *
 * Offers the same PDFs the analytics page download menu offers for this hospital
 * (`hospital.modules.analyticsPDFs`), plus the Detailed Report, which is the built-in default.
 */
const AnalyticsReportPdfSelect = ({ name, value, onChange, ...rest }: AnalyticsReportPdfSelectProps) => {
  const scope = useScope();
  const enabledPdfs: AnalyticsPdfOption[] | undefined = scope?.hospital?.modules?.analyticsPDFs;

  const options = useMemo(() => {
    const enabled =
      enabledPdfs && enabledPdfs.length > 0
        ? analyticsPdfsOptions.filter(option => enabledPdfs.some(pdf => pdf?.value === option.value))
        : [defaultAnalyticsPdfOption];
    // Detailed Report is always offered (it is what gets sent when nothing is configured). It goes
    // last so the first option has a non-empty value; SelectInput keys its option lookup off that.
    return [...enabled.filter(option => option.value !== detailedReportPdfOption.value), detailedReportPdfOption];
  }, [enabledPdfs]);

  return (
    <SelectInput
      {...rest}
      name={name}
      options={options}
      value={value ?? ''}
      defaultValue=""
      onChange={(next: string | undefined) => onChange(next ?? '')}
      isClearable={false}
      isSearchable={false}
    />
  );
};

export default AnalyticsReportPdfSelect;
